
import { useEffect, useMemo, useState } from "react";
import Loader from "../../../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Box, Checkbox, Typography } from "@mui/material";
import {
  updateVoucherPaymentData,
  setShowThankYouMessage,
  setisUpgradePreferentialStep,
  setActiveStep,
  getVoucherById,
  setEditPreviewApplicant,
  setUnitPrefEditFromThankYou,
} from "../voucher-form.slice";
import * as yup from "yup";
import { useFormik } from "formik";
import CommonButtonVoucherForm from "../../../shared-components/common-button/CommonButtonVoucherForm";
import { route } from "../../../services/apiRoutes";
import TermsAndConditionsModal from "./TermsAndConditionsModal";
import MultiTransaction from "./payment-options/PaymentOptionsForm";
import CancellationDetails from "./payment-options/CancellationDetails";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import {
  PaymentbuildPayload,
  setFormValuesFromApplicantData,
  formatIndianNumber,
} from "./common-functions/PaymentCommonFunctions";
import { EOIFinanceStatus, EOIPaymentMode, FORM_PHASE, OFFLINE_PAYMENT_METHODS, SHOW_PAYMENT_METHOD_OPTION } from "../../../config/constant";
import { getPayableAmount } from "./common-functions/EoiAmountHelpers";
import { useTranslation } from "react-i18next";

const PaymentOptions = () => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [termsAndConditionModal, setTermsAndConditionModal] = useState(false);
  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const dispatch = useDispatch();
  const { t } = useTranslation("common", {
    keyPrefix: "corporatePaymentOption",
  });
  const { voucherId } = useParams();
  const [totalAmountPayable, setTotalAmountPayable] = useState("");


  const { applicantData, activeStep, isLoading, isUpgradePreferentialStep, eoiCampaignDetails } =
    useSelector((state) => state?.voucherForm);
  const Amount = getPayableAmount(applicantData, eoiCampaignDetails, isUpgradePreferentialStep);
  const TotalAmount = Amount ? Number(Amount)?.toFixed(0)?.toString() : 0;

  const originalTransactions = useMemo(() => {
    return applicantData?.payments?.map(tx => ({
      id: tx.id,
      paymentMethod: tx.paymentDetails?.method || "",
      offlineRadioOptions: tx.paymentDetails?.method || "",
      chequeDDNumber: tx.paymentDetails?.chequeNumber || "",
      transactionNumber: tx.paymentDetails?.transactionNumber || "",
      bankName: tx.paymentDetails?.drawnOn || "",
      date: tx.date || "",
      amount: String(tx.paidAmount || ""),
      paymentProof: tx.paymentDetails?.paymentProof?.[0] || null,
      lastFourDigits: tx.paymentDetails?.lastFourDigits || "",
    })) || [];
  }, [applicantData?.payments]);

  const termsAndConditionsdData =
    applicantData?.termsAndCondition;

  const validateUniqueTransactionNumbers = (values) => {
    const errors = {};
    const transactions = values.transactions || [];

    const usedChequeNumbers = new Map();
    const usedTxnAndGatewayIds = new Map();

    transactions.forEach((txn, index) => {
      const transactionNo = txn.transactionNo?.trim();
      const gatewayPaymentId = txn.gatewayPaymentId?.trim();

      if (
        txn.offlineRadioOptions === OFFLINE_PAYMENT_METHODS.CHEQUE &&
        transactionNo
      ) {
        if (usedChequeNumbers.has(transactionNo)) {
          errors.transactions = errors.transactions || [];
          errors.transactions[index] = errors.transactions[index] || {};
          errors.transactions[index].transactionNo = t(
            "corporatePaymentValidations.duplicateChequeNumber",
          );
        } else {
          usedChequeNumbers.set(transactionNo, index);
        }
      }

      if (
        txn.offlineRadioOptions !== OFFLINE_PAYMENT_METHODS.CHEQUE &&
        transactionNo
      ) {
        if (usedTxnAndGatewayIds.has(transactionNo)) {
          errors.transactions = errors.transactions || [];
          errors.transactions[index] = errors.transactions[index] || {};
          errors.transactions[index].transactionNo = t(
            "corporatePaymentValidations.duplicateTransaction",
          );
        } else {
          usedTxnAndGatewayIds.set(transactionNo, index);
        }
      }

      if (gatewayPaymentId) {
        if (usedTxnAndGatewayIds.has(gatewayPaymentId)) {
          errors.transactions = errors.transactions || [];
          errors.transactions[index] = errors.transactions[index] || {};
          errors.transactions[index].gatewayPaymentId = t(
            "corporatePaymentValidations.duplicateTransaction",
          );
        } else {
          usedTxnAndGatewayIds.set(gatewayPaymentId, index);
        }
      }
    });

    return errors;
  };

  const paymentOptionsFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      cancellationPayeename: "",
      cancellationBankname: "",
      cancellationIfscCode: "",
      residentStatus: applicantData?.applicant1?.personalDetails?.residentStatus || "",
      cancellationSwiftCode: "",
      cancellationAccountNo: "",
      cancellationAccountType: "",
      cancelledCheque: [],
      isPhysicalCancelledCheque: false,
      termsAndConditions: false,
    },
    validationSchema: yup.object().shape({
      // Radio button
      // Cancellation/Refund shared fields across all payment methods
      cancellationPayeename: yup
        .string()
        .matches(/^[A-Za-z ]+$/, "Payee name must contain only letters")
        .required("Payee name is required"),
      cancellationBankname: yup
        .string()
        .matches(/^[A-Za-z ]+$/, "Bank name must contain only letters")
        .required("Bank name is required"),
      cancellationIfscCode: yup
        .string()
        .matches(
          /^[A-Z]{4}0[A-Z0-9]{6}$/,
          "Enter a valid IFSC code (eg. SBIN0001537)"
        )
        .required("IFSC code is required"),
      cancellationSwiftCode: yup
        .string().optional()
        .matches(
          /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
          "Enter valid SWIFT Code (e.g. HDFCINBB, SBININBBXXX)"
        ),
      cancellationAccountNo: yup
        .string()
        .matches(
          /^\d{9,}$/,
          "Account number must be numeric and at least 9 digits"
        )
        .required("Account number is required"),
      cancellationAccountType: yup
        .string()
        .required("Account type is required"),
      cancelledCheque: yup.array().when("isPhysicalCancelledCheque", {
        is: false,
        then: (schema) =>
          schema
            .min(1, "Please upload Cancelled Cheque or Bank Statement")
            .required("Cancelled Cheque or Bank Statement is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      // Terms
      termsAndConditions: yup
        .boolean()
        .oneOf([true], "You must agree to the Terms & Conditions"),
    }),
    onSubmit: (values) => {
      //console.log("Form Values:", values);
      // dispatch API call or submit handler
    },
  });

  const MultipaymentOptionsFormik = useFormik({
    initialValues: {
      transactions: [
        {
          id: "",
          paymentMethod: SHOW_PAYMENT_METHOD_OPTION ? "" : EOIPaymentMode.GATEWAY, // Online | Offline
          offlineRadioOptions: "",
          bankName: "",
          branchName: "",
          accountNo: "",
          amount: TotalAmount,
          paymentProof: [],
          status: EOIFinanceStatus.UNVERIFIED,
          date: null,
          type: "",
          isPaid: false,
          showUploadPopup: false,
          isPhysicalPaymentProof: false,
          email: "",
          onlineMethod: "",
          contact: "",
          orderId: "",
          gatewayPaymentId: "",
          paymentTransactionId: "",
          paidAmount: "",
          lastFourDigits: "",
        },
      ],
    },
    validate: (values) => {
      return validateUniqueTransactionNumbers(values);
    },
    validationSchema: yup.object().shape({
      transactions: yup.array().of(
        yup.object().shape({
          paymentMethod: SHOW_PAYMENT_METHOD_OPTION ? yup.string().required("Payment method is required") : yup.string().notRequired(),

          // Always required
          amount: yup
            .number()
            .typeError("Amount must be numeric")
            .required("Amount is required")
            .moreThan(0, "Amount must be greater than 0"),

          isPaid: yup.boolean().when("paymentMethod", {
            is: EOIPaymentMode.GATEWAY,
            then: (schema) =>
              schema.oneOf([true], t("corporatePaymentValidations.isPaid.required")),
            otherwise: (schema) => schema.notRequired(),
          }),

          // Offline-specific validations
          offlineRadioOptions: yup.string().when("paymentMethod", {
            is: EOIPaymentMode.OFFLINE,
            then: (schema) =>
              schema.required("Please select an offline option"),
            otherwise: (schema) => schema.notRequired(),
          }),

          transactionNo: yup
            .string()
            .when(["paymentMethod", "offlineRadioOptions"], {
              is: (paymentMethod, offlineRadioOptions) =>
                paymentMethod === EOIPaymentMode.OFFLINE &&
                offlineRadioOptions === "CHEQUE",
              then: (schema) =>
                schema
                  .required("Cheque/DD number is required")
                  .matches(
                    /^\d{6}$/,
                    "Cheque/DD number must be exactly 6 digits"
                  ),
              otherwise: (schema) =>
                schema.when("paymentMethod", {
                  is: EOIPaymentMode.OFFLINE,
                  then: (s) =>
                    s
                      .required("Transaction ID is required"),
                  otherwise: (s) => s.notRequired(),
                }),
            }),

          bankName: yup
            .string()
            .when(["paymentMethod", "offlineRadioOptions"], {
              is: (paymentMethod, offlineRadioOptions) =>
                paymentMethod === EOIPaymentMode.OFFLINE &&
                offlineRadioOptions === "CHEQUE",
              then: (schema) =>
                schema
                  .required("Bank name is required")
                  .matches(
                    /^[A-Za-z ]+$/,
                    "Bank Name must contain only letters"
                  ),
              otherwise: (schema) => schema.notRequired(),
            }),

          branchName: yup.string().notRequired(),
          accountNo: yup.string().notRequired(),

          // accountNo: yup.string().when("paymentMethod", {
          //   is: EOIPaymentMode.OFFLINE,
          //   then: (schema) =>
          //     schema
          //       .matches(/^\d{9,}$/, "Account number must be at least 9 digits")
          //       .required("Account number is required"),
          //   otherwise: (schema) => schema.notRequired(),
          // }),

          paymentProof: yup
            .array()
            .when("paymentMethod", {
              is: EOIPaymentMode.OFFLINE,
              then: (schema) =>
                schema
                  .min(1, "Payment proof is required")
                  .required("Payment proof is required"),
              otherwise: (schema) => schema.notRequired(),
            }),

          date: yup
            .date()
            .typeError("Date is invalid")
            .when("paymentMethod", {
              is: EOIPaymentMode.OFFLINE,
              then: (schema) => schema.required("Date is required"),
              otherwise: (schema) => schema.notRequired(),
            }),
          lastFourDigits: yup.string().when(
            "offlineRadioOptions",
            {
              is: "EDC MACHINE",
              then: (schema) =>
                schema
                  .required("Last 4 digits of card are required")
                  .matches(/^\d{4}$/, "Please enter the last 4 digits of the card (numbers only)"),
              otherwise: (schema) => schema.notRequired(),
            }
          )
        })
      ),
    }),

    onSubmit: (values) => {
      //console.log("Form Values:", values);
      // dispatch API call or submit handler
    },
  });

  useEffect(() => {
    paymentOptionsFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentOptionsFormik?.values]);

  const handlePrevious = () => {
    dispatch(setActiveStep(activeStep - 1));
  };

  // Helper function to create the other details payload

  useEffect(() => {
    paymentOptionsFormik.setErrors({});
    paymentOptionsFormik.setTouched({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentOptionsFormik.values.paymentMethod]);


  // Function to build final payload
  const handleSaveForm = async () => {
    // ✅ Validate both forms
    const multiErrors = await MultipaymentOptionsFormik.validateForm();
    const cancelErrors = await paymentOptionsFormik.validateForm();

    // ✅ If errors exist, mark fields as touched so error messages show
    if (
      Object.keys(multiErrors).length > 0 ||
      Object.keys(cancelErrors).length > 0
    ) {
      // 🔹 Proper nested touched for multipayment form
      const touchedTransactions =
        MultipaymentOptionsFormik.values.transactions.map((tx) =>
          Object.fromEntries(
            Object.keys(tx).map((k) => [k, true]) // mark each field in transaction as touched
          )
        );

      MultipaymentOptionsFormik.setTouched({
        transactions: touchedTransactions,
      });

      // 🔹 Touch cancel form fields
      paymentOptionsFormik.setTouched(
        Object.fromEntries(
          Object.keys(paymentOptionsFormik.values).map((k) => [k, true])
        )
      );

      return; // ❌ Stop here, don’t submit payload
    }

    setIsSubmitDisabled(true);
    try {
      const payload = PaymentbuildPayload(
        MultipaymentOptionsFormik.values,
        paymentOptionsFormik.values,
        totalAmountPayable,
        false,
        activeStep,
        originalTransactions,
      );

      const res = await dispatch(
        updateVoucherPaymentData({
          path: `${route.VOUCHER_UPDATE_PAYMENT_DATA}/${voucherId}`,
          payload,
          opportunityId: voucherId,
        })
      ).unwrap();

      // Navigate only on success response
      if (res?.status === 200 || res?.status === 201) {
        dispatch(setShowThankYouMessage(true));
        dispatch(setisUpgradePreferentialStep(false));
        dispatch(setUnitPrefEditFromThankYou(false));
        dispatch(setActiveStep(0))
        dispatch(setEditPreviewApplicant(false))
        globalThis.location.href = `/voucher-form/thank-you/${voucherId}`;
        return;
      }

      // If not successful, show error instead of success
      setSnackbarConfig({
        open: true,
        message: res?.message || "Failed to update payment details",
        severity: "error",
      });
    } catch (error) {
      setSnackbarConfig({
        open: true,
        message: error?.errors?.message || "Failed to update payment details",
        severity: "error",
      });
      console.error("Payment update failed:", error);
    } finally {
      setIsSubmitDisabled(false);
    }
  };

  useEffect(() => {
    if (voucherId) {
      dispatch(getVoucherById(`/${voucherId}`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherId]);

  useEffect(() => {
    if (applicantData) {
      setFormValuesFromApplicantData(
        applicantData,
        applicantData?.payments,
        MultipaymentOptionsFormik,
        paymentOptionsFormik,
        applicantData.isAgreedOnTerms
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData]);

  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "",
    });
  };



  const getTitle = () => {
    if (!applicantData) return "Voucher Value";

    const { formPhase } = applicantData;

    if (formPhase === FORM_PHASE.VOUCHER) {
      if (!isUpgradePreferentialStep) return "Voucher Value";
    }

    if (formPhase === FORM_PHASE.EOI) {
      return "EOI Value";
    }

    return "Voucher Value"; // default fallback
  };

  useEffect(() => {
    const totalAmount = getPayableAmount(applicantData, eoiCampaignDetails, isUpgradePreferentialStep);
    setTotalAmountPayable(totalAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {isLoading && <Loader isLoading={isLoading} />}

      <Box
        my={2}
        py={1}
        px={3}
        sx={{
          width: "fit-content",
          margin: "12px auto",
          border: "1px solid rgba(26, 64, 125, 0.3)",
          borderRadius: "16px",
          textAlign: "center",
          boxShadow: "rgba(26, 64, 125, 0.3)",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ color: "#1A407D", fontWeight: 500 }}
        >
          {getTitle()}
        </Typography>
        <Typography
          sx={{ fontSize: "18px", fontWeight: 600, color: "#1C252E" }}
        >
          ₹{formatIndianNumber(getPayableAmount(applicantData, eoiCampaignDetails, isUpgradePreferentialStep))}
        </Typography>
      </Box>

      <Typography
        sx={{
          color: "#1C252E",
          fontSize: "16px",
          fontWeight: "600",
          lineHeight: "36px",
          mb: "12px",
          textAlign: "center",
        }}
      >
        Payment Options
      </Typography>

      <form
        onSubmit={paymentOptionsFormik.handleSubmit}
        onReset={paymentOptionsFormik.handleReset}
      >
        <MultiTransaction paymentOptionsFormik={MultipaymentOptionsFormik} />
        <CancellationDetails paymentOptionsFormik={paymentOptionsFormik} />

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Checkbox
            sx={{ pl: 0 }}
            name="termsAndConditions"
            checked={paymentOptionsFormik.values.termsAndConditions}
            onChange={paymentOptionsFormik.handleChange}
            onBlur={paymentOptionsFormik.handleBlur}
          />
          <Typography variant="body2">
            I Agree to the{" "}
            <button
              className="resetButton"
              style={{
                color: "#1A407D",
                textDecoration: "underline",
              }}
              onClick={() => setTermsAndConditionModal(true)}
            >
              Terms & Conditions
            </button>
          </Typography>
        </Box>
        {paymentOptionsFormik.touched.termsAndConditions &&
          paymentOptionsFormik.errors.termsAndConditions && (
            <Typography variant="caption" color="error">
              {paymentOptionsFormik.errors.termsAndConditions}
            </Typography>
          )}
        <TermsAndConditionsModal
          termsAndConditionModal={termsAndConditionModal}
          setTermsAndConditionModal={setTermsAndConditionModal}
          termsAndConditionsData={termsAndConditionsdData}
        />

        <CommonButtonVoucherForm
          isSubmit
          handleNext={handleSaveForm}
          isDisable={isSubmitDisabled}
          handlePrevious={handlePrevious}
          saveDraftPath={`${route.VOUCHER_UPDATE_PAYMENT_DATA}/${voucherId}`}
          saveDraftData={{
            ...PaymentbuildPayload(
              MultipaymentOptionsFormik.values,
              paymentOptionsFormik.values,
              totalAmountPayable,
              true,
              activeStep,
              originalTransactions,
            ),
            lastStep: Math.max(applicantData?.lastStep, activeStep),
          }}
        />

        <SnackbarAlert
          open={snackbarConfig.open}
          message={snackbarConfig.message}
          severity={snackbarConfig.severity}
          onClose={handleSnackbarClose}
        />
      </form>
    </div>
  );
};

export default PaymentOptions;
